import { RewardedBookOffer } from "../ads/RewardedBookOffer";
import { SummaryCard } from "./SummaryCard";
import { useEffect, useMemo, useRef, useState } from "react";
import {
	createTripFilmMoment,
	featureTripFilmMoment,
	tripFilmTimeline,
	type TripFilmMoment,
} from "@japangolmok/core";
import { customerSectionLabels } from "../content/customer-language";
import { serializeTripFilm } from "../storage/trips";
import { BrandMark } from "../ui/BrandMark";
import { Layout, usePageMeta } from "../ui/Shell";
import {
	downloadTripFile,
	TripClonePanel,
	TripExportPanel,
} from "./TripTransfer";
import {
	LoadingTrip,
	MissingTrip,
	TripStorageError,
	useTripWorkspace,
} from "./useTripWorkspace";

const dateLabel = (date: string) =>
	new Intl.DateTimeFormat("ko-KR", {
		month: "long",
		day: "numeric",
		timeZone: "Asia/Tokyo",
	}).format(new Date(`${date}T12:00:00+09:00`));
async function readLocalPhoto(file: File) {
	if (
		!["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
		file.size > 2_500_000
	)
		throw new Error("INVALID_FILM_PHOTO");
	return new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onerror = () => reject(new Error("PHOTO_READ_FAILED"));
		reader.onload = () =>
			typeof reader.result === "string"
				? resolve(reader.result)
				: reject(new Error("PHOTO_READ_FAILED"));
		reader.readAsDataURL(file);
	});
}
function useOnlineStatus() {
	const [online, setOnline] = useState(
		() => typeof navigator === "undefined" || navigator.onLine,
	);
	useEffect(() => {
		const update = () => setOnline(navigator.onLine);
		window.addEventListener("online", update);
		window.addEventListener("offline", update);
		return () => {
			window.removeEventListener("online", update);
			window.removeEventListener("offline", update);
		};
	}, []);
	return online;
}
function useReducedMotion() {
	const [reduced, setReduced] = useState(false);
	useEffect(() => {
		if (!window.matchMedia) return;
		const media = window.matchMedia("(prefers-reduced-motion: reduce)");
		const update = () => setReduced(media.matches);
		update();
		media.addEventListener?.("change", update);
		return () => media.removeEventListener?.("change", update);
	}, []);
	return reduced;
}
function FilmFrame({
	moment,
	index,
	total,
	compact = false,
	onFeature,
	onDelete,
	onPhoto,
}: {
	moment: TripFilmMoment;
	index: number;
	total: number;
	compact?: boolean;
	onFeature: () => void;
	onDelete: () => void;
	onPhoto: (file?: File) => void;
}) {
	const [failed, setFailed] = useState(false);
	useEffect(() => setFailed(false), [moment.photoDataUrl]);
	return (
		<article className={`film-frame ${compact ? "compact" : ""}`}>
			<div className="film-frame-count">
				{String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
			</div>
			{moment.photoDataUrl && !failed ? (
				<img
					src={moment.photoDataUrl}
					onError={() => setFailed(true)}
					alt={`${moment.placeTitle}에서 사용자가 고른 장면`}
				/>
			) : (
				<div
					className="film-photo-missing"
					role="img"
					aria-label={`${moment.placeTitle} 사진 없이 남긴 장면`}
				>
					<BrandMark size={56} />
					<strong>
						{failed ? "사진을 다시 읽지 못했어요." : "사진 없이 남긴 장면"}
					</strong>
					<span>
						장소와 한 줄은 남아 있어요. 아래에서 사진을 다시 고를 수 있어요.
					</span>
				</div>
			)}
			<div className="film-caption">
				<span>
					{dateLabel(moment.dayDate)} · {moment.neighborhood}
					{moment.planItemId === null && " · 일정 밖의 발견"}
				</span>
				<h3>{moment.placeTitle}</h3>
				{moment.note && <p>{moment.note}</p>}
				<div className="film-frame-actions">
					{moment.featured ? (
						<strong>이 날의 대표 장면</strong>
					) : (
						<button className="text-button" type="button" onClick={onFeature}>
							대표로 고르기
						</button>
					)}
					<label className="button secondary file-button">
						사진 다시 고르기
						<input
							className="sr-only"
							type="file"
							accept="image/jpeg,image/png,image/webp"
							onChange={(event) => {
								onPhoto(event.currentTarget.files?.[0]);
								event.currentTarget.value = "";
							}}
						/>
					</label>
					<button
						className="text-button danger"
						type="button"
						onClick={onDelete}
					>
						장면 삭제
					</button>
				</div>
			</div>
		</article>
	);
}

export function TripFilmPage({ id }: { id: string }) {
	const { state, persist, retry, saveError, saving } = useTripWorkspace(id);
	const loadedTrip = state.status === "ready" ? state.trip : null;
	const [cadence, setCadence] = useState<"trip" | "day">("trip");
	const [activeIndex, setActiveIndex] = useState(0);
	const [playing, setPlaying] = useState(false);
	const [photo, setPhoto] = useState<{
		data: string;
		mime: TripFilmMoment["photoMimeType"];
	} | null>(null);
	const [selectedKey, setSelectedKey] = useState("");
	const [notice, setNotice] = useState("");
	const [filmPreview, setFilmPreview] = useState<{
		source: typeof loadedTrip;
		html: string;
	} | null>(null);
	const photoRead = useRef(0);
	const submitting = useRef(false);
	const online = useOnlineStatus();
	const reducedMotion = useReducedMotion();
	usePageMeta(
		loadedTrip
			? `${loadedTrip.title} 골목 필름 — 일본골목`
			: "여행 골목 필름 — 일본골목",
		"계획했던 동네와 뜻밖의 발견을 사진과 한 줄로 이어 보는 나만의 여행책이에요.",
	);
	const timeline = useMemo(
		() => (loadedTrip ? tripFilmTimeline(loadedTrip) : []),
		[loadedTrip],
	);
	const candidates = useMemo(
		() =>
			loadedTrip
				? loadedTrip.days.flatMap((day) =>
						day.items.map((item) => ({
							key: `${day.date}:${item.id}`,
							dayDate: day.date,
							itemId: item.id,
							title: item.title,
							neighborhood: item.area || loadedTrip.destinationCity,
							time: item.time,
						})),
					)
				: [],
		[loadedTrip],
	);
	const groups = useMemo(
		() =>
			loadedTrip
				? loadedTrip.days.map((day) => ({
						date: day.date,
						moments: timeline.filter((moment) => moment.dayDate === day.date),
					}))
				: [],
		[loadedTrip, timeline],
	);
	useEffect(() => {
		if (!playing || reducedMotion || timeline.length < 2) return;
		const timer = window.setInterval(
			() => setActiveIndex((current) => (current + 1) % timeline.length),
			2800,
		);
		return () => window.clearInterval(timer);
	}, [playing, reducedMotion, timeline.length]);
	useEffect(() => {
		if (activeIndex >= timeline.length)
			setActiveIndex(Math.max(0, timeline.length - 1));
	}, [activeIndex, timeline.length]);
	if (state.status === "loading")
		return <LoadingTrip label="골목 필름을 불러오는 중" />;
	if (state.status === "storage_error")
		return <TripStorageError retry={retry} />;
	if (state.status === "not_found") return <MissingTrip />;
	const trip = state.trip;
	const freeScene = selectedKey === "free" || candidates.length === 0;
	const addMoment = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (submitting.current) return;
		const form = event.currentTarget;
		const data = new FormData(form);
		const selected = candidates.find(
			(candidate) => candidate.key === selectedKey,
		);
		if (!freeScene && !selected) {
			setNotice("일정의 장면을 고르거나 일정 밖 발견을 선택해 주세요.");
			return;
		}
		submitting.current = true;
		try {
			const dayDate = freeScene
				? String(data.get("dayDate"))
				: selected!.dayDate;
			const moment = createTripFilmMoment(
				trip,
				{
					planItemId: freeScene ? null : selected!.itemId,
					dayDate,
					placeTitle: freeScene
						? String(data.get("placeTitle")).trim()
						: undefined,
					neighborhood: freeScene
						? String(data.get("neighborhood")).trim()
						: undefined,
					note: String(data.get("note") ?? "").trim(),
					capturedAt: `${dayDate}T${selected?.time ?? "12:00"}:00+09:00`,
					photoDataUrl: photo?.data ?? null,
					photoMimeType: photo?.mime ?? null,
				},
				new Date().toISOString(),
			);
			if (
				!(await persist((draft) => {
					draft.filmMoments = [...(draft.filmMoments ?? []), moment];
				}))
			)
				return;
			++photoRead.current;
			setPhoto(null);
			form.reset();
			setSelectedKey("");
			setNotice("고른 장면과 한 줄을 이 기기에 남겼어요.");
		} catch {
			setNotice(
				"장소·동네·여행 날짜와 사진을 확인해 주세요. 작성한 내용은 그대로예요.",
			);
		} finally {
			submitting.current = false;
		}
	};
	const selectPhoto = async (file?: File) => {
		const token = ++photoRead.current;
		if (!file) {
			setNotice("사진 선택을 취소했어요. 먼저 골랐던 사진은 그대로예요.");
			return;
		}
		try {
			const data = await readLocalPhoto(file);
			if (token === photoRead.current) {
				setPhoto({ data, mime: file.type as TripFilmMoment["photoMimeType"] });
				setNotice(
					"이 기기에서만 사진을 읽었어요. 아래에서 확인하고 장면을 남겨 주세요.",
				);
			}
		} catch {
			if (token === photoRead.current)
				setNotice(
					"JPG·PNG·WebP 사진을 2.5MB까지 읽을 수 있어요. 먼저 골랐던 사진과 입력은 바꾸지 않았어요.",
				);
		}
	};
	const replacePhoto = async (moment: TripFilmMoment, file?: File) => {
		if (!file) {
			setNotice("사진 선택을 취소했어요. 원래 장면은 그대로예요.");
			return;
		}
		try {
			const data = await readLocalPhoto(file);
			if (
				!(await persist((draft) => {
					const target = draft.filmMoments?.find(
						(item) => item.id === moment.id,
					);
					if (!target) throw new Error("MISSING_MOMENT");
					target.photoDataUrl = data;
					target.photoMimeType = file.type as TripFilmMoment["photoMimeType"];
				}))
			)
				return;
			setNotice("장소와 한 줄은 그대로 두고 사진을 바꿨어요.");
		} catch {
			setNotice(
				"사진을 읽지 못했어요. JPG·PNG·WebP 2.5MB 이하인지 확인해 주세요. 원래 장면은 그대로예요.",
			);
		}
	};
	const removeMoment = async (moment: TripFilmMoment) => {
		if (
			!window.confirm(
				`‘${moment.placeTitle}’ 장면을 이 기기에서 지울까요? 여행 일정은 남아 있어요.`,
			)
		)
			return;
		if (
			await persist((draft) => {
				draft.filmMoments = (draft.filmMoments ?? []).filter(
					(entry) => entry.id !== moment.id,
				);
			})
		)
			setNotice("장면과 저장된 사진 사본을 지웠어요.");
	};
	const featureMoment = async (moment: TripFilmMoment) => {
		if (
			await persist((draft) => {
				draft.filmMoments = featureTripFilmMoment(
					draft.filmMoments ?? [],
					moment.id,
				);
			})
		)
			setNotice(`${dateLabel(moment.dayDate)} 대표 장면으로 골랐어요.`);
	};
	const frame = (moment: TripFilmMoment, compact = false) => (
		<FilmFrame
			compact={compact}
			key={moment.id}
			moment={moment}
			index={timeline.indexOf(moment)}
			total={timeline.length}
			onFeature={() => void featureMoment(moment)}
			onDelete={() => void removeMoment(moment)}
			onPhoto={(file) => void replacePhoto(moment, file)}
		/>
	);
	const active = timeline[activeIndex];
	const unvisited = [
		...trip.days.flatMap((day) =>
			day.items.map((item) => ({ item, key: `${day.date}:${item.id}` })),
		),
		...(trip.candidates ?? []).map((item) => ({
			item,
			key: `candidate:${item.id}`,
		})),
	].filter(({ item }) => !item.completed || item.returnReason);
	const currentPreview = filmPreview?.source === trip ? filmPreview : null;
	return (
		<Layout>
			<section className="film-hero">
				<a className="back-link" href={`/trips/${encodeURIComponent(trip.id)}`}>
					← 여행 전체
				</a>
				<span className="eyebrow">나만의 골목 필름</span>
				<h1>
					{trip.title}
					<br />
					골목 필름
				</h1>
				<p>
					계획했던 동네와 뜻밖의 발견을 내가 고른 사진과 한 줄로 이어요.
					사람·얼굴을 분석하지 않고 사진과 위치를 외부로 보내지 않아요.
				</p>
				<div className="film-route">
					<span>{trip.origin}</span>
					<i />
					<strong>{trip.destinationCity}</strong>
					<i />
					<span>
						{trip.startDate}–{trip.endDate}
					</span>
				</div>
			</section>
			{!online && (
				<div className="film-offline" role="status">
					<strong>오프라인이에요.</strong> 저장된 여행과 사진은 그대로 볼 수
					있고, 새 사진도 이 기기에만 남아요.
				</div>
			)}
			{notice && (
				<div className="toast" role="status">
					{notice}
				</div>
			)}
			{saveError && (
				<p className="inline-error" role="alert">
					{saveError}
				</p>
			)}
			<section className="section film-section">
				<div className="section-heading">
					<div>
						<span className="eyebrow">{customerSectionLabels.film.view}</span>
						<h2>
							여행 전체로 흐르거나,
							<br />
							하루씩 천천히 봐요.
						</h2>
					</div>
					<div className="film-toolbar">
						<div className="film-cadence" aria-label="필름 보기 단위">
							<button
								type="button"
								aria-pressed={cadence === "trip"}
								onClick={() => setCadence("trip")}
							>
								여행 전체
							</button>
							<button
								type="button"
								aria-pressed={cadence === "day"}
								onClick={() => setCadence("day")}
							>
								하루씩
							</button>
						</div>
						<button
							className="button secondary"
							type="button"
							disabled={timeline.length < 2 || reducedMotion}
							aria-pressed={playing}
							onClick={() => setPlaying((value) => !value)}
						>
							{reducedMotion
								? "움직임 줄이기 적용 중"
								: playing
									? "필름 멈추기"
									: "필름 재생"}
						</button>
					</div>
				</div>
				{timeline.length === 0 ? (
					<div className="film-empty">
						<BrandMark size={72} />
						<h3>아직 고른 골목 장면이 없어요.</h3>
						<p>
							일정에 담은 곳도, 걷다 만난 곳도 아래에서 직접 남길 수 있어요.
						</p>
						<a href="#film-editor" className="button primary">
							첫 장면 남기기
						</a>
						<a
							className="button secondary"
							href={`/trips/${trip.id}#itinerary`}
						>
							일정에 장소 담기
						</a>
					</div>
				) : cadence === "trip" && active ? (
					<div className="film-player" aria-live={playing ? "off" : "polite"}>
						{frame(active)}
						<div className="film-player-nav">
							<button
								className="button secondary"
								type="button"
								onClick={() =>
									setActiveIndex(
										(activeIndex - 1 + timeline.length) % timeline.length,
									)
								}
							>
								← 이전 장면
							</button>
							<span>
								{activeIndex + 1} / {timeline.length}
							</span>
							<button
								className="button secondary"
								type="button"
								onClick={() =>
									setActiveIndex((activeIndex + 1) % timeline.length)
								}
							>
								다음 장면 →
							</button>
						</div>
					</div>
				) : (
					<div className="film-day-groups">
						{groups.map((group) => (
							<section key={group.date}>
								<header>
									<span>{dateLabel(group.date)}</span>
									<strong>{group.moments.length}장</strong>
								</header>
								{group.moments.length ? (
									<div className="film-day-grid">
										{group.moments.map((moment) => frame(moment, true))}
									</div>
								) : (
									<p>이 날은 아직 고른 장면이 없어요.</p>
								)}
							</section>
						))}
					</div>
				)}
			</section>
			<section className="section film-editor" id="film-editor">
				<div>
					<span className="eyebrow">{customerSectionLabels.film.addScene}</span>
					<h2>걷다가 만난 장면도 남겨요.</h2>
					<p>
						일정 밖의 가게와 한 줄도 괜찮아요. 사진을 고르지 않아도 텍스트는
						남길 수 있어요.
					</p>
				</div>
				<form className="editor-panel compact-form" onSubmit={addMoment}>
					{candidates.length > 0 && (
						<label>
							동네·가게·날짜
							<select
								name="candidate"
								required
								value={selectedKey}
								onChange={(event) => setSelectedKey(event.target.value)}
							>
								<option value="" disabled>
									장면을 고르세요
								</option>
								<option value="free">일정 밖에서 만난 곳</option>
								{candidates.map((candidate) => (
									<option value={candidate.key} key={candidate.key}>
										{candidate.dayDate} · {candidate.neighborhood} ·{" "}
										{candidate.title}
									</option>
								))}
							</select>
						</label>
					)}
					{freeScene && (
						<>
							<label>
								장면을 남긴 날
								<select name="dayDate">
									{trip.days.map((day) => (
										<option key={day.date} value={day.date}>
											{day.date}
										</option>
									))}
								</select>
							</label>
							<label>
								만난 장소
								<input
									name="placeTitle"
									required
									maxLength={80}
									placeholder="예: 걷다가 들른 작은 책방"
								/>
							</label>
							<label>
								동네
								<input
									name="neighborhood"
									required
									maxLength={60}
									defaultValue={trip.destinationCity}
								/>
							</label>
						</>
					)}
					<label>
						기억하고 싶은 한 줄 <span className="optional">선택</span>
						<textarea
							name="note"
							maxLength={1000}
							placeholder="무엇이 좋아서 기억하고 싶나요?"
						/>
					</label>
					<label className="film-photo-input">
						사진{" "}
						<span className="optional">선택 · JPG/PNG/WebP 2.5MB 이하</span>
						<input
							className="sr-only"
							type="file"
							accept="image/jpeg,image/png,image/webp"
							onChange={(event) => {
								void selectPhoto(event.currentTarget.files?.[0]);
								event.currentTarget.value = "";
							}}
						/>
						<span className="button secondary">사진 파일 고르기</span>
					</label>
					{photo && (
						<>
							<img
								className="film-photo-preview"
								src={photo.data}
								alt="저장 전 선택한 사진 미리보기"
							/>
							<button
								className="button text"
								type="button"
								onClick={() => {
									++photoRead.current;
									setPhoto(null);
								}}
							>
								선택한 사진 빼기
							</button>
						</>
					)}
					<button className="button primary" type="submit" disabled={saving}>
						{photo ? "사진과 장면 남기기" : "사진 없이 장면 남기기"}
					</button>
					<small>사진과 한 줄은 이 기기에만 저장해요.</small>
				</form>
			</section>
			{unvisited.length > 0 && (
				<section className="section">
					<h2>다음에 이어 걸을 곳</h2>
					<p>기록한 장면과 아직 다녀오지 않은 후보를 구분해요.</p>
					<ul>
						{unvisited.map(({ item, key }) => (
							<li key={key}>
								<strong>{item.title}</strong>
								<p>{item.returnReason || "아직 다녀오지 않은 곳"}</p>
							</li>
						))}
					</ul>
				</section>
			)}
			<section className="section film-next">
				<div>
					<span className="eyebrow">{customerSectionLabels.film.keep}</span>
					<h2>
						파일로 보관하고,
						<br />
						다음 골목으로 이어 가요.
					</h2>
					<p>
						여행책은 사진과 날짜·한 줄을 포함해요. 먼저 미리 보고 이 기기에 받을
						수 있어요. 다시 가져올 여행 사본은 아래에서 따로 만들어요.
					</p>
				</div>
				<div className="keep-actions">
					<button
						className="button primary"
						type="button"
						disabled={!timeline.length}
						onClick={() => {
							try {
								setFilmPreview({ source: trip, html: serializeTripFilm(trip) });
							} catch {
								setNotice(
									"여행책을 만들지 못했어요. 저장된 장면을 확인해 주세요.",
								);
							}
						}}
					>
						골목 필름 파일 받기
					</button>
					<a className="button text" href={`/trips/${trip.id}#keep`}>
						여행 전체 보관·삭제
					</a>
				</div>
				{currentPreview && (
					<div className="transfer-preview">
						<h3>여행책 미리보기</h3>
						<iframe
							title="골목 여행책 파일 미리보기"
							srcDoc={currentPreview.html}
							sandbox=""
							width="100%"
							height="480"
						/>
						<div className="button-row">
							<button
								className="button primary"
								type="button"
								onClick={() =>
									downloadTripFile(
										"japangolmok-film.html",
										currentPreview.html,
										"text/html",
									)
								}
							>
								확인한 여행책 파일 받기
							</button>
							<button
								className="button secondary"
								type="button"
								onClick={() => setFilmPreview(null)}
							>
								여행책 미리보기 닫기
							</button>
						</div>
					</div>
				)}
			</section>
			<section className="section">
				<SummaryCard trip={trip} />
				<RewardedBookOffer trip={trip} persist={persist} />
				<TripExportPanel trip={trip} />
				<TripClonePanel trip={trip} />
			</section>
		</Layout>
	);
}
